import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, Image as ImageIcon, Plus, Trash2, Upload, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm, UseFormSetError } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  ClientItem,
  clientsService,
  CreateClientPayload,
  UpdateClientPayload,
} from '../../../services/clients.service'
import { FormError } from '../../ui/FormError'
import { ImagePreviewModal } from '../../ui/ImagePreviewModal'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Input } from '../../ui/input'
import { Textarea } from '../../ui/textarea'

const addressSchema = z.object({
  street: z.string(),
  exteriorNumber: z.string(),
  interiorNumber: z.string(),
  postalCode: z.string(),
  neighborhood: z.string(),
  municipality: z.string(),
  city: z.string(),
  state: z.string(),
  country: z.string(),
  reference: z.string(),
})

const requiredAddressSchema = addressSchema.extend({
  street: z.string().min(1, 'La calle es requerida'),
  exteriorNumber: z.string().min(1, 'El número exterior es requerido'),
  postalCode: z.string().min(1, 'El código postal es requerido'),
  neighborhood: z.string().min(1, 'La colonia es requerida'),
  municipality: z.string().min(1, 'El municipio es requerido'),
  city: z.string().min(1, 'La ciudad es requerida'),
  state: z.string().min(1, 'El estado es requerido'),
  country: z.string().min(1, 'El país es requerido'),
})

const optionalEmailSchema = z
  .string()
  .trim()
  .refine(
    (value) => value === '' || z.string().email().safeParse(value).success,
    'Debe ser un email válido',
  )

function hasAddressInput(address: z.input<typeof addressSchema>) {
  return Object.entries(address).some(([field, value]) => {
    const normalized = value.trim()

    return field === 'country' ? normalized !== 'México' : normalized !== ''
  })
}

const normalizePhone = (value: string) => value.replace(/[^0-9]+/g, '')

const baseClientSchema = z.object({
  name: z.string().trim().min(1, 'El nombre es requerido'),
  phones: z
    .array(
      z.object({
        value: z.string().trim().min(1, 'El teléfono no puede estar vacío'),
      }),
    )
    .min(1, 'Al menos un teléfono es requerido'),
  emails: z.array(
    z.object({
      value: optionalEmailSchema,
    }),
  ),
  addressEnabled: z.boolean(),
  address: addressSchema,
  comments: z.string(),
})

const clientSchema = baseClientSchema.superRefine((values, context) => {
  const seenPhones = new Set<string>()
  values.phones.forEach(({ value }, index) => {
    const normalized = normalizePhone(value)
    if (!normalized || !seenPhones.has(normalized)) {
      if (normalized) seenPhones.add(normalized)
      return
    }

    context.addIssue({
      code: 'custom',
      path: ['phones', index, 'value'],
      message: 'No puedes registrar dos veces el mismo teléfono para un cliente.',
    })
  })

  const seenEmails = new Set<string>()
  values.emails.forEach(({ value }, index) => {
    const normalized = value.trim().toLowerCase()
    if (!normalized || !seenEmails.has(normalized)) {
      if (normalized) seenEmails.add(normalized)
      return
    }

    context.addIssue({
      code: 'custom',
      path: ['emails', index, 'value'],
      message: 'No puedes registrar dos veces el mismo correo para un cliente.',
    })
  })

  if (!values.addressEnabled || !hasAddressInput(values.address)) return

  const parsed = requiredAddressSchema.safeParse(values.address)
  if (parsed.success) return

  parsed.error.issues.forEach((issue) => {
    context.addIssue({
      ...issue,
      path: ['address', ...issue.path],
    })
  })
})

type ClientFormValues = z.input<typeof baseClientSchema>
type ClientFormMode = 'create' | 'edit'

interface ClientModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (client: ClientItem) => void
}

interface EditClientModalProps extends ClientModalProps {
  client: ClientItem | null
}

interface ClientFormModalProps extends ClientModalProps {
  mode: ClientFormMode
  client?: ClientItem | null
}

const emptyAddress = (): ClientFormValues['address'] => ({
  street: '',
  exteriorNumber: '',
  interiorNumber: '',
  postalCode: '',
  neighborhood: '',
  municipality: '',
  city: '',
  state: '',
  country: 'México',
  reference: '',
})

function createDefaults(): ClientFormValues {
  return {
    name: '',
    phones: [{ value: '' }],
    emails: [{ value: '' }],
    addressEnabled: true,
    address: emptyAddress(),
    comments: '',
  }
}

function editDefaults(client: ClientItem): ClientFormValues {
  const address = client.address

  return {
    name: client.name,
    phones: client.phones.map((value) => ({ value })),
    emails: client.emails.map((value) => ({ value })),
    addressEnabled: client.address !== null && client.address !== undefined,
    address: address
      ? {
        street: address.street,
        exteriorNumber: address.exteriorNumber,
        interiorNumber: address.interiorNumber ?? '',
        postalCode: address.postalCode,
        neighborhood: address.neighborhood,
        municipality: address.municipality,
        city: address.city,
        state: address.state,
        country: address.country,
        reference: address.reference ?? '',
      }
      : emptyAddress(),
    comments: client.notes ?? '',
  }
}

function backendMessage(caught: unknown, fallback: string): string {
  const response = caught as {
    response?: {
      data?: { message?: string; errors?: Record<string, string[]> }
    }
  }
  const message = response.response?.data?.message

  const validationMessage = response.response?.data?.errors
    ? Object.values(response.response.data.errors).flat().find(Boolean)
    : null

  return String(
    validationMessage || message || fallback,
  )
}

function applyServerValidation(
  caught: unknown,
  setError: UseFormSetError<ClientFormValues>,
): void {
  const response = caught as {
    response?: { data?: { errors?: Record<string, string[]> } }
  }

  Object.entries(response.response?.data?.errors ?? {}).forEach(([field, messages]) => {
    const message = messages?.[0]
    if (!message) return

    if (field === 'phone') {
      setError('phones.0.value', { type: 'server', message })
      return
    }

    const contact = field.match(/^(phones|emails)\.(\d+)$/)
    if (contact) {
      setError(`${contact[1]}.${contact[2]}.value` as 'phones.0.value' | 'emails.0.value', {
        type: 'server',
        message,
      })
    }
  })
}

export function NewClientModal(props: ClientModalProps) {
  return <ClientFormModal {...props} mode="create" />
}

export function EditClientModal({ client, ...props }: EditClientModalProps) {
  return <ClientFormModal {...props} mode="edit" client={client} />
}

function ClientFormModal({
  mode,
  client,
  open,
  onOpenChange,
  onSuccess,
}: ClientFormModalProps) {
  const [profile, setProfile] = useState<ClientItem | null>(null)
  const [profileAttempt, setProfileAttempt] = useState(0)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [profileError, setProfileError] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [expandedImage, setExpandedImage] = useState<string | null>(null)
  const [removeExistingAvatar, setRemoveExistingAvatar] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientSchema),
    defaultValues: createDefaults(),
  })

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({ control, name: 'phones' })
  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({ control, name: 'emails' })
  const addressEnabled = watch('addressEnabled')

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl)
    }
  }, [imagePreviewUrl])

  useEffect(() => {
    if (!open || mode !== 'edit' || !client) return

    let active = true
    setIsLoadingProfile(true)
    setProfileError(false)
    setProfile(null)

    clientsService
      .getItem(client.id)
      .then((current) => {
        if (active) setProfile(current)
      })
      .catch(() => {
        if (active) setProfileError(true)
      })
      .finally(() => {
        if (active) setIsLoadingProfile(false)
      })

    return () => {
      active = false
    }
  }, [client, mode, open, profileAttempt])

  useEffect(() => {
    if (!open) return

    if (mode === 'create') {
      reset(createDefaults())
      setProfile(null)
    } else if (profile) {
      reset(editDefaults(profile))
    }

    setImageFile(null)
    setImagePreviewUrl(null)
    setExpandedImage(null)
    setRemoveExistingAvatar(false)
  }, [mode, open, profile, reset])

  const selectedProfile = mode === 'edit' ? profile : null
  const existingAvatarUrl = selectedProfile?.avatarUrl ?? null
  const visibleAvatarUrl =
    imagePreviewUrl ?? (removeExistingAvatar ? null : existingAvatarUrl)
  const imageInputId = mode === 'create' ? 'client-image' : 'client-edit-image'
  const replaceInputId =
    mode === 'create' ? 'client-image-replace' : 'client-edit-image-replace'

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImagePreviewUrl(URL.createObjectURL(file))
      setExpandedImage(null)
      setRemoveExistingAvatar(false)
    }
    event.target.value = ''
  }

  const clearImageState = () => {
    setImageFile(null)
    setImagePreviewUrl(null)
    setExpandedImage(null)
    setRemoveExistingAvatar(false)
  }

  const removeVisibleAvatar = () => {
    setExpandedImage(null)

    if (imageFile) {
      setImageFile(null)
      setImagePreviewUrl(null)
      return
    }

    if (existingAvatarUrl) setRemoveExistingAvatar(true)
  }

  const handleModalOpenChange = (isOpen: boolean) => {
    if (!isOpen && isSaving) return
    if (!isOpen) {
      reset(createDefaults())
      clearImageState()
      setProfile(null)
      setProfileError(false)
    }
    onOpenChange(isOpen)
  }

  const onSubmit = async (values: ClientFormValues) => {
    try {
      setIsSaving(true)
      const address =
        values.addressEnabled && hasAddressInput(values.address)
          ? values.address
          : null
      const emails = values.emails
        .map(({ value }) => value.trim())
        .filter(Boolean)
      let saved: ClientItem

      if (mode === 'create') {
        const payload: CreateClientPayload = {
          name: values.name,
          emails,
          phones: values.phones.map(({ value }) => value),
          address,
          notes: values.comments,
          avatar: imageFile,
          is_active: true,
        }
        saved = await clientsService.createItem(payload)
        toast.success('Cliente registrado exitosamente')
      } else {
        if (!selectedProfile) return
        const payload: UpdateClientPayload = {
          name: values.name,
          emails,
          phones: values.phones.map(({ value }) => value),
          address,
          notes: values.comments,
          avatar: imageFile,
          remove_avatar: removeExistingAvatar && !imageFile,
        }
        saved = await clientsService.updateItem(selectedProfile.id, payload)
        toast.success('Cliente actualizado exitosamente')
      }

      reset(createDefaults())
      clearImageState()
      onOpenChange(false)
      onSuccess?.(saved)
    } catch (caught: unknown) {
      applyServerValidation(caught, setError)
      toast.error(
        backendMessage(
          caught,
          mode === 'create'
            ? 'No se pudo registrar el cliente'
            : 'No se pudo actualizar el cliente',
        ),
      )
    } finally {
      setIsSaving(false)
    }
  }

  const title = mode === 'create' ? 'Nuevo Cliente' : 'Editar Cliente'

  return (
    <Dialog open={open} onOpenChange={handleModalOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            {mode === 'create'
              ? 'Formulario para registrar un nuevo cliente'
              : 'Formulario para editar el perfil del cliente'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'edit' && (isLoadingProfile || profileError) ? (
          <div className="flex min-h-64 flex-col items-center justify-center gap-4 text-center">
            {isLoadingProfile ? (
              <p className="text-sm text-slate-500">
                Cargando perfil actualizado...
              </p>
            ) : (
              <>
                <p className="text-sm text-red-600">
                  No se pudo cargar el perfil del cliente.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setProfileAttempt((attempt) => attempt + 1)}
                >
                  Reintentar
                </Button>
              </>
            )}
          </div>
        ) : (
          <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Nombre Completo <span className="text-red-600">*</span>
              </label>
              <Input
                placeholder="Ej: Juan Pérez García"
                {...register('name')}
              />
              <FormError id="client-name-error" message={errors.name?.message} />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                  Teléfonos <span className="text-red-600">*</span>
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => appendPhone({ value: '' })}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar Teléfono
                </Button>
              </div>
              <div className="space-y-2">
                {phoneFields.map((field, index) => (
                  <div key={field.id} className="flex flex-col gap-1">
                    <div className="flex gap-2">
                      <Input
                        placeholder="555-1234"
                        {...register(`phones.${index}.value` as const)}
                      />
                      {phoneFields.length > 1 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label={`Eliminar teléfono ${index + 1}`}
                          onClick={() => removePhone(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <FormError
                      id={`client-phone-${index}-error`}
                      message={errors.phones?.[index]?.value?.message}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                  Correos Electrónicos
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => appendEmail({ value: '' })}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar Email
                </Button>
              </div>
              <div className="space-y-2">
                {emailFields.length === 0 && (
                  <p className="rounded-md border border-dashed border-slate-200 p-3 text-sm text-slate-500">
                    Sin correos registrados.
                  </p>
                )}
                {emailFields.map((field, index) => (
                  <div key={field.id} className="flex flex-col gap-1">
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="correo@ejemplo.com"
                        {...register(`emails.${index}.value` as const)}
                        aria-describedby={
                          errors.emails?.[index]?.value
                            ? `client-email-${index}-error`
                            : undefined
                        }
                      />
                      {(mode === 'edit' || emailFields.length > 1) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label={`Eliminar email ${index + 1}`}
                          onClick={() => removeEmail(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <FormError
                      id={`client-email-${index}-error`}
                      message={errors.emails?.[index]?.value?.message}
                    />
                  </div>
                ))}
                <FormError message={errors.emails?.root?.message} />
              </div>
            </div>

            <div className="space-y-3 rounded-lg bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <h4 className="font-medium text-slate-900">Dirección</h4>
                {mode === 'edit' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setValue('addressEnabled', !addressEnabled)}
                  >
                    {addressEnabled ? 'Quitar dirección' : 'Agregar dirección'}
                  </Button>
                )}
              </div>

              {addressEnabled ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <AddressFields register={register} errors={errors} />
                </div>
              ) : (
                <p className="rounded-md border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-500">
                  El cliente no tendrá una dirección registrada.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Comentarios
              </label>
              <Textarea
                placeholder="Notas adicionales sobre el cliente..."
                rows={3}
                {...register('comments')}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Imagen del Cliente
              </label>
              {visibleAvatarUrl ? (
                <div className="group relative inline-block">
                  <img
                    src={visibleAvatarUrl}
                    alt={
                      imageFile
                        ? 'Vista previa del cliente'
                        : 'Avatar actual del cliente'
                    }
                    className="h-40 w-40 rounded-full border border-slate-200 object-cover shadow-sm"
                  />
                  <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <button
                      type="button"
                      onClick={() => setExpandedImage(visibleAvatarUrl)}
                      className="rounded-full bg-white/90 p-1.5 shadow transition hover:bg-white"
                      title="Ver imagen"
                      aria-label={
                        mode === 'create'
                          ? 'Ver imagen seleccionada'
                          : 'Ver imagen del cliente'
                      }
                    >
                      <Eye
                        className="h-4 w-4 text-slate-700"
                        aria-hidden="true"
                      />
                    </button>
                    <label
                      htmlFor={replaceInputId}
                      className="cursor-pointer rounded-full bg-white/90 p-1.5 shadow transition hover:bg-white"
                      title="Cambiar imagen"
                    >
                      <Upload
                        className="h-4 w-4 text-slate-700"
                        aria-hidden="true"
                      />
                      <span className="sr-only">
                        Cambiar imagen del cliente
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={removeVisibleAvatar}
                      className="rounded-full bg-red-500/90 p-1.5 shadow transition hover:bg-red-600"
                      title="Eliminar imagen"
                      aria-label={
                        imageFile
                          ? 'Eliminar imagen seleccionada'
                          : 'Eliminar avatar actual'
                      }
                    >
                      <X className="h-4 w-4 text-white" aria-hidden="true" />
                    </button>
                  </div>
                  <input
                    id={replaceInputId}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  {imageFile && (
                    <span className="mt-1 block max-w-[160px] truncate text-xs text-slate-500">
                      {imageFile.name}
                    </span>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center transition hover:border-slate-300">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id={imageInputId}
                  />
                  <label htmlFor={imageInputId} className="cursor-pointer">
                    <ImageIcon className="mx-auto mb-2 h-12 w-12 text-slate-400" />
                    <p className="text-sm text-slate-600">
                      Haz clic para subir imagen
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      PNG, JPG hasta 5MB
                    </p>
                  </label>
                  {removeExistingAvatar && existingAvatarUrl && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="mt-2"
                      onClick={() => setRemoveExistingAvatar(false)}
                    >
                      Conservar imagen actual
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 border-t border-slate-200 pt-4">
              <Button type="submit" disabled={isSaving} className="flex-1">
                {isSaving
                  ? 'Guardando...'
                  : mode === 'create'
                    ? 'Guardar Cliente'
                    : 'Guardar Cambios'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={isSaving}
                onClick={() => handleModalOpenChange(false)}
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </DialogContent>

      <ImagePreviewModal
        open={expandedImage !== null}
        onOpenChange={(isOpen) => !isOpen && setExpandedImage(null)}
        imageUrl={expandedImage}
        altText={
          mode === 'create'
            ? 'Avatar seleccionado ampliado'
            : 'Avatar del cliente ampliado'
        }
      />
    </Dialog>
  )
}

function AddressFields({
  register,
  errors,
}: {
  register: ReturnType<typeof useForm<ClientFormValues>>['register']
  errors: ReturnType<typeof useForm<ClientFormValues>>['formState']['errors']
}) {
  return (
    <>
      <div className="space-y-2 md:col-span-2">
        <label className="text-sm font-medium text-slate-700">Calle</label>
        <Input
          placeholder="Av. Insurgentes"
          aria-invalid={Boolean(errors.address?.street)}
          aria-describedby={errors.address?.street ? 'client-street-error' : undefined}
          {...register('address.street')}
        />
        <FormError id="client-street-error" message={errors.address?.street?.message} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">
          Número Exterior
        </label>
        <Input placeholder="123" {...register('address.exteriorNumber')} />
        <FormError message={errors.address?.exteriorNumber?.message} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">
          Número Interior
        </label>
        <Input
          placeholder="4B (opcional)"
          {...register('address.interiorNumber')}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">
          Código Postal
        </label>
        <Input placeholder="06700" {...register('address.postalCode')} />
        <FormError message={errors.address?.postalCode?.message} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Colonia</label>
        <Input placeholder="Roma Norte" {...register('address.neighborhood')} />
        <FormError message={errors.address?.neighborhood?.message} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Municipio</label>
        <Input placeholder="Cuauhtémoc" {...register('address.municipality')} />
        <FormError message={errors.address?.municipality?.message} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Ciudad</label>
        <Input placeholder="Ciudad de México" {...register('address.city')} />
        <FormError message={errors.address?.city?.message} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Estado</label>
        <Input placeholder="CDMX" {...register('address.state')} />
        <FormError message={errors.address?.state?.message} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">País</label>
        <Input placeholder="México" {...register('address.country')} />
        <FormError message={errors.address?.country?.message} />
      </div>
      <div className="space-y-2 md:col-span-3">
        <label className="text-sm font-medium text-slate-700">Referencia</label>
        <Input
          placeholder="Frente al parque, edificio azul..."
          {...register('address.reference')}
        />
      </div>
    </>
  )
}
